import { AlertCircle, CheckCircle, Info, XCircle } from "lucide-react";
import type { Dispatch, ReactNode } from "react";
import { createContext, useCallback, useContext, useReducer } from "react";
import { strings } from "@/I18n";

const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 5000;

type ToastVariant = "default" | "success" | "error" | "warning" | "info";

type Toast = {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
};

type ToastAction =
  | { type: "ADD_TOAST"; toast: Toast }
  | { type: "REMOVE_TOAST"; id: string }
  | { type: "DISMISS_TOAST"; id: string };

interface ToastState {
  toasts: Toast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string, dispatch: Dispatch<ToastAction>) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: "REMOVE_TOAST", id: toastId });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

const reducer = (state: ToastState, action: ToastAction): ToastState => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "DISMISS_TOAST": {
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };
    }

    case "REMOVE_TOAST":
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };

    default:
      return state;
  }
};

type ToastContextValue = {
  toasts: Toast[];
  toast: (message: string, variant?: ToastVariant) => void;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { toasts: [] });

  const toast = useCallback(
    (message: string, variant: ToastVariant = "default") => {
      let finalMessage = message;
      if (!message || (typeof message === "string" && !message.trim())) {
        if (variant === "error") {
          finalMessage = strings("errors.somethingWentWrong");
        } else if (variant === "success") {
          finalMessage = strings("common.done");
        } else if (variant === "warning") {
          finalMessage = strings("common.warning");
        } else {
          return;
        }
      }
      const id = genId();
      dispatch({
        type: "ADD_TOAST",
        toast: { id, message: finalMessage, variant },
      });
      addToRemoveQueue(id, dispatch);
    },
    []
  );

  const dismiss = useCallback((id: string) => {
    dispatch({ type: "DISMISS_TOAST", id });
  }, []);

  return (
    <ToastContext.Provider value={{ toasts: state.toasts, toast, dismiss }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export const getToastIcon = (variant: ToastVariant) => {
  switch (variant) {
    case "success":
      return <CheckCircle className="tw-h-5 tw-w-5 tw-text-success" />;
    case "error":
      return <XCircle className="tw-h-5 tw-w-5 tw-text-destructive" />;
    case "warning":
      return <AlertCircle className="tw-h-5 tw-w-5 tw-text-warning" />;
    case "info":
      return <Info className="tw-h-5 tw-w-5 tw-text-info" />;
    default:
      return null;
  }
};

export const getToastStyles = (variant: ToastVariant) => {
  switch (variant) {
    case "success":
      return "tw-border-success/30 tw-bg-[#f0fdf4]";
    case "error":
      return "tw-border-destructive/30 tw-bg-[#fef2f2]";
    case "warning":
      return "tw-border-warning/30 tw-bg-[#fffbeb]";
    case "info":
      return "tw-border-info/30 tw-bg-[#eff6ff]";
    default:
      return "tw-border-border tw-bg-background";
  }
};

export type { Toast, ToastVariant };
